import { useRef } from 'react';
import {
	DataKYCFileTctWithFile,
	GetKYCInvestorInstanceRes,
	KYCFileType,
} from '@dealstack/shared/lib/generated';
import { Step } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ManageKYC/types';
import { useUploadFile } from './useUploadFile';
import { associateKycFileToInvestorViaMip } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import {
	STEP_TYPE_TO_KYC_FILE_TYPE,
	UploadFileOptions,
} from './useKycFileUploadAndAssociate.types';

interface Props {
	selectedKycFile?: DataKYCFileTctWithFile;
	currentInvestor?: GetKYCInvestorInstanceRes;
	currentStep?: Step;
	kycWorkflowId?: string;
	typeOverride?: KYCFileType;
	onUploadedAndFileAssociatedSuccess: () => void;
}

export const useKycFileUploadAndAssociate = ({
	selectedKycFile,
	currentInvestor,
	currentStep,
	kycWorkflowId,
	typeOverride,
	onUploadedAndFileAssociatedSuccess,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	// Store the values that were active when upload was initiated
	const uploadContextRef = useRef<{
		selectedKycFile?: DataKYCFileTctWithFile;
		currentInvestor?: GetKYCInvestorInstanceRes;
		currentStep?: Step;
		kycWorkflowId?: string;
		templateIdOverride?: string;
		typeOverride?: KYCFileType;
	} | null>(null);

	// file upload hook
	const { mutate: originalUploadFile, isPending: isFileUploading } = useUploadFile({
		onError: () => {
			showToastV2({
				title: tMipManage('kyc.upload_failed'),
				state: 'error',
			});
		},
		onSuccess: (uploadedFile) => {
			// Use the captured values from when the upload was initiated
			const context = uploadContextRef.current;
			if (!context) {
				return;
			}

			// once the file is uploaded, associate it to the investor
			// this prevents the association from being associated with the wrong investor or step
			updateKycFileAssociation({
				fileId: uploadedFile?.id,
				userId: context.currentInvestor?.user?.id,
				type:
					context.typeOverride ??
					(context.currentStep
						? STEP_TYPE_TO_KYC_FILE_TYPE[context.currentStep.type]
						: undefined),
				templateId: context.templateIdOverride,
				kycWorkflowId: context.kycWorkflowId,
			});
		},
	});

	// wrapper function that captures current values at upload time
	const uploadFile = (file: File, options?: UploadFileOptions) => {
		// Capture the current values at the moment of upload
		uploadContextRef.current = {
			selectedKycFile,
			currentInvestor,
			currentStep,
			kycWorkflowId,
			templateIdOverride: options?.templateIdOverride,
			typeOverride,
		};

		originalUploadFile(file);
	};

	const { mutate: updateKycFileAssociation, isPending: isUpdatingKycFileAssociation } =
		useMutation({
			mutationFn: (variables: Parameters<typeof associateKycFileToInvestorViaMip>[0]) =>
				associateKycFileToInvestorViaMip(variables),
			onSuccess: () => {
				onUploadedAndFileAssociatedSuccess();
				if (uploadContextRef.current?.templateIdOverride) {
					return Promise.all([
						queryClient.invalidateQueries({
							queryKey: [
								API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_INVESTOR_FORMS_GET,
							],
						}),
						queryClient.invalidateQueries({
							queryKey: [
								API_PATHS.DEAL_DEALID_MIP_PROGRAMMEID_DOCUMENT_WORKFLOW_KYC_FORMS_KYC_WORKFLOW_KYCWORKFLOWID_GET,
							],
						}),
					]);
				}
			},
		});

	const isLoading = isFileUploading || isUpdatingKycFileAssociation;

	return {
		uploadFile,
		isLoading,
	};
};
